/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import styled from '@emotion/styled';

import { Typography2 } from '@coursera/cds-core';
import { color, fontFamily } from '@coursera/coursera-ui';

import { org_coursera_skill_GoalType as GoalType } from 'bundles/enterprise-admin-skills/components/__generated__/globalTypes';
import type { FunctionMapping, SkillMapping } from 'bundles/enterprise-admin-skills/components/types';
import {
  formatDepartmentName,
  formatSkillName,
  proficiencyFilter,
} from 'bundles/enterprise-admin-skills/components/utils';
import { SkillSetCardHeader } from 'bundles/enterprise-skillset-bank/components/SkillSetCardHeader';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import {
  ExtraProficiencies,
  Proficiencies,
  ProficiencyRow,
} from 'bundles/enterprise-skillset-bank/components/SkillSetCardProficiencies';
import { isInBeta } from 'bundles/enterprise-skillset-bank/utils/academies';
import { BetaBadge, Clickable, Spacer, truncate } from 'bundles/enterprise-ui';
import type { SkillSet as LearnerSkillSet } from 'bundles/naptimejs/resources/skillSets.v1';

import _t from 'i18n!nls/enterprise-admin-skills';

type SkillSet = LearnerSkillSet;

const UnselectedCard = styled.div({
  border: `1px solid ${color.divider}`,
  background: color.white,
  color: color.primaryText,
  borderRadius: '3px',
  padding: '12px',
  height: '100%',
  minHeight: '471px',
  display: 'flex',
  flexDirection: 'column',
});

const SelectedCard = styled.div({
  border: `1px solid ${color.primary}`,
  background: color.white,
  color: color.primaryText,
  borderRadius: '3px',
  padding: '12px',
  height: '100%',
  minHeight: '471px',
  display: 'flex',
  flexDirection: 'column',
});

export const CardContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  padding: '12px 18px 3px',
  height: '100%',
  minHeight: 'inherit',
});

export const Footer = styled.div({
  borderTop: `1px solid ${color.divider}`,
  paddingTop: '15px',
  marginTop: 'auto',
  justifySelf: 'end',
});

export const Card: React.LegacyFunctionComponentWithChildren<{ isSelected?: boolean }> = ({ isSelected, ...props }) => {
  if (isSelected) {
    return <SelectedCard {...props} />;
  } else {
    return <UnselectedCard {...props} />;
  }
};
export const SecondaryBadge = styled.div({
  background: '#EFEFEF',
  borderRadius: '100px',
  padding: '6px 15px',
  whiteSpace: 'nowrap',
  marginTop: '-6px',
});

export const Title = styled.h3({
  fontSize: '24px',
  lineHeight: '28px',
  color: color.primary,
  fontFamily: fontFamily.body,
});

export const getGoalType = (type?: string | null) => {
  switch (type) {
    case GoalType.LITERACY:
      return _t('Literacy');
    case GoalType.UPSKILL:
      return _t('Upskill');
    case GoalType.RESKILL:
      return _t('Reskill');
    default:
      return '';
  }
};

const SkillSetCard: React.LegacyFunctionComponentWithChildren<{
  skillSet: SkillSet;
  onTitleClick?: () => void;
  notification?: React.ReactNode;
  isAlreadyInTheOrganization?: boolean;
  isSelected?: boolean;
  functionMappings?: FunctionMapping[];
  skillMappings?: SkillMapping[];
  isProgramScope?: boolean;
}> = ({
  skillSet,
  onTitleClick,
  notification,
  isAlreadyInTheOrganization,
  isSelected,
  children,
  functionMappings,
  skillMappings,
  isProgramScope,
}) => {
  const { targetProficiencies = [] } = skillSet;
  const proficiencies = targetProficiencies.filter(proficiencyFilter);
  const title = skillSet.targetSkillProfileName;
  const skillsLimit = 6;
  const isInteractive = Boolean(onTitleClick);
  return (
    <Card data-e2e="skillset-card" isSelected={isSelected}>
      {notification && (
        <div>
          {notification}
          <Spacer size={12} />
        </div>
      )}
      <CardContent>
        <Clickable
          onClick={onTitleClick}
          data-e2e={`skillset-card-title-${skillSet.id}`}
          tabIndex={isInteractive ? 0 : -1}
        >
          <Title>
            {truncate(title, 65)}
            {(skillSet.academyIds ?? []).some(isInBeta) && <BetaBadge css={{ marginLeft: 'var(--cds-spacing-150)' }} />}
          </Title>
        </Clickable>
        <Spacer size={12} />
        <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography2 component="p" color="supportText">
            {[formatDepartmentName(skillSet.functionId, functionMappings), getGoalType(skillSet.lure)]
              .filter((_) => _)
              .join(' - ')}
          </Typography2>
          {isAlreadyInTheOrganization && !isProgramScope && (
            <SecondaryBadge>
              <Typography2 component="p" color="supportText">
                {_t('Already in your organization')}
              </Typography2>
            </SecondaryBadge>
          )}
          {!isAlreadyInTheOrganization && isProgramScope && (
            <SecondaryBadge>
              <Typography2 component="p" color="supportText">
                {_t('New to your organization')}
              </Typography2>
            </SecondaryBadge>
          )}
        </div>
        <Spacer size={12} />
        <SkillSetCardHeader skillCount={proficiencies?.length} />
        <Proficiencies>
          {proficiencies.slice(0, skillsLimit).map((proficiency, index) => {
            const { skillId } = proficiency;
            const name = formatSkillName(skillId, skillMappings);
            return (
              <ProficiencyRow
                key={`${proficiency.skillId}-${String(index)}`}
                name={name}
                target={proficiency.targetProficiency}
              />
            );
          })}
          {proficiencies.length > skillsLimit && (
            <ExtraProficiencies extraCount={proficiencies.length - skillsLimit} onViewDetails={onTitleClick} />
          )}
        </Proficiencies>
        {children && <Footer>{children}</Footer>}
      </CardContent>
    </Card>
  );
};

export default SkillSetCard;
